import { observable, action } from "mobx";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { RATE_WIDGET_IDS, CATEGORY_ENDPOINT_MAP } from "../utils/widgetConstants";
import { resolveDatePreset } from "../utils/datePresetUtils";
import { resolveChartData } from "../utils/chartDataUtils";

const LATEST_REPLIES_WIDGET_ID = "LATEST_REPLIES";

class WidgetDataStore {
  @observable widgetData = {};
  @observable loadingByCategory = {};
  @observable activeDateFilter = "7d";
  @observable activeMemberIds = [];

  @action
  setDateFilter = preset => {
    this.activeDateFilter = preset;
  };

  @action
  setMemberIds = ids => {
    this.activeMemberIds = ids || [];
  };

  @action
  setLoadingForCategory = (category, value) => {
    this.loadingByCategory = { ...this.loadingByCategory, [category]: value };
  };

  @action
  setWidgetData = (widgetId, data) => {
    this.widgetData = { ...this.widgetData, [widgetId]: data };
  };

  getWidgetData = widgetId => {
    return this.widgetData[widgetId] || null;
  };

  isCategoryLoading = category => {
    return !!this.loadingByCategory[category];
  };

  @action
  clearWidgetData = widgetId => {
    const updated = { ...this.widgetData };
    delete updated[widgetId];
    this.widgetData = updated;
  };

  fetchSingleWidget = async widget => {
    if (!widget?.widgetId) return;
    const { fromDate, toDate } = resolveDatePreset(this.activeDateFilter);
    const memberIds = this.activeMemberIds;
    if (widget.widgetId === LATEST_REPLIES_WIDGET_ID) {
      this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, true);
      await this.fetchLatestReplies(fromDate, toDate, memberIds);
    } else if (RATE_WIDGET_IDS.has(widget.widgetId)) {
      this.setLoadingForCategory(widget.widgetId, true);
      await this.fetchRateSeriesData(widget.widgetId, fromDate, toDate, memberIds);
    } else {
      this.setLoadingForCategory(widget.category, true);
      await this.fetchCategoryData(widget.category, [widget.widgetId], fromDate, toDate, memberIds);
    }
  };

  @action
  fetchAllWidgetData = async widgets => {
    if (!widgets?.length) return;

    const { fromDate, toDate } = resolveDatePreset(this.activeDateFilter);
    const memberIds = this.activeMemberIds;

    // Group non-rate widgets by category
    const categoryMap = {};
    const rateWidgets = [];
    let hasLatestRepliesWidget = false;

    widgets.forEach(w => {
      if (w.widgetId === LATEST_REPLIES_WIDGET_ID) {
        hasLatestRepliesWidget = true;
        this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, true);
      } else if (RATE_WIDGET_IDS.has(w.widgetId)) {
        rateWidgets.push(w);
        this.setLoadingForCategory(w.widgetId, true);
      } else {
        if (!categoryMap[w.category]) categoryMap[w.category] = [];
        categoryMap[w.category].push(w.widgetId);
      }
    });

    Object.keys(categoryMap).forEach(cat => this.setLoadingForCategory(cat, true));

    const categoryFetches = Object.entries(categoryMap).map(
      ([category, widgetIds]) =>
        this.fetchCategoryData(category, widgetIds, fromDate, toDate, memberIds)
    );

    const rateFetches = rateWidgets.map(w =>
      this.fetchRateSeriesData(w.widgetId, fromDate, toDate, memberIds)
    );

    const latestRepliesFetch = hasLatestRepliesWidget
      ? [this.fetchLatestReplies(fromDate, toDate, memberIds)]
      : [];

    await Promise.all([...categoryFetches, ...rateFetches, ...latestRepliesFetch]);
  };

  fetchCategoryData = async (category, widgetIds, fromDate, toDate, memberIds) => {
    const urlKey = CATEGORY_ENDPOINT_MAP[category];
    if (!urlKey || !URLS[urlKey]) return;

    try {
      const res = await makeApi({
        url: URLS[urlKey],
        method: "POST",
        data: { widgetIds, fromDate, toDate, memberIds }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error(`DashboardV2: fetchCategoryData(${category}) failed`, data?.error);
        toasterState.setToastMsg(`Failed to load ${category.toLowerCase()} widget data`, "fail");
        return;
      }
      if (data) {
        widgetIds.forEach(widgetId => {
          this.setWidgetData(widgetId, { ...data, _category: category });
        });
      }
    } catch (err) {
      console.error(`DashboardV2: fetchCategoryData(${category}) error`, err);
      toasterState.setToastMsg(`Failed to load ${category.toLowerCase()} widget data`, "fail");
    } finally {
      this.setLoadingForCategory(category, false);
    }
  };

  fetchLatestReplies = async (fromDate, toDate, memberIds) => {
    try {
      const res = await makeApi({
        url: URLS.outbox,
        method: "POST",
        data: {
          pageNo: 1,
          limit: 5,
          sortDirection: "DESC",
          sortParam: "REPLY_DATE",
          filters: {
            memberIds: memberIds || [],
            filterKey: "REPLY_COUNT",
            channel: ["EMAIL", "LINKEDIN"],
            replied: { fromDate, toDate },
            statuses: [],
            all: false
          }
        }
      });
      // The backend returns a null body (not an empty object) when there are no matching
      // replies in the period - that's a normal "zero results" case, not a failure, so it
      // must still resolve to an empty list rather than skipping setWidgetData entirely
      // (skipping it would leave getWidgetData() returning null forever, which the loading
      // check in WidgetGrid.js reads as "still loading").
      const data = res?.data;
      this.setWidgetData(LATEST_REPLIES_WIDGET_ID, { replies: data?.result || [] });
    } catch (err) {
      console.error("DashboardV2: fetchLatestReplies error", err);
      this.setWidgetData(LATEST_REPLIES_WIDGET_ID, { replies: [] });
    } finally {
      this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, false);
    }
  };

  fetchRateSeriesData = async (widgetId, fromDate, toDate, memberIds) => {
    try {
      const res = await makeApi({
        url: URLS.getDashboardWidgetsRateSeries,
        method: "POST",
        data: { widgetId, fromDate, toDate, memberIds }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error(`DashboardV2: fetchRateSeriesData(${widgetId}) failed`, data?.error);
        toasterState.setToastMsg(data?.error || "Failed to load rate widget data", "fail");
        return;
      }
      if (data) {
        const chartData = resolveChartData(data, this.activeDateFilter);
        this.setWidgetData(widgetId, {
          currentRate: data.currentRate,
          previousRate: data.previousRate,
          trend: data.trend,
          chartData,
          series: data.series || [],
          _isRate: true
        });
      }
    } catch (err) {
      console.error(`DashboardV2: fetchRateSeriesData(${widgetId}) error`, err);
      toasterState.setToastMsg("Failed to load rate widget data", "fail");
    } finally {
      this.setLoadingForCategory(widgetId, false);
    }
  };
}

const widgetDataStore = new WidgetDataStore();
export { widgetDataStore };
export default widgetDataStore;
